import { useState, FormEvent } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import './Contact.scss';

interface FormData {
  name: string;
  email: string;
  company: string;
  budget: string;
  message: string;
  website: string; // honeypot field
}

interface FormErrors {
  name?: string;
  email?: string;
  message?: string;
}

interface FormStatus {
  type: 'idle' | 'loading' | 'success' | 'error';
  message?: string;
}

const budgetOptions = [
  { value: '', label: 'Select a budget range' },
  { value: 'under-5k', label: 'Under $5,000' },
  { value: '5k-15k', label: '$5,000 - $15,000' },
  { value: '15k-50k', label: '$15,000 - $50,000' },
  { value: '50k-plus', label: '$50,000+' },
  { value: 'not-sure', label: 'Not sure yet' },
];

function Contact() {
  const [formData, setFormData] = useState<FormData>({
    name: '',
    email: '',
    company: '',
    budget: '',
    message: '',
    website: '', // honeypot - should remain empty
  });
  const [errors, setErrors] = useState<FormErrors>({});
  const [touched, setTouched] = useState<Record<string, boolean>>({});
  const [status, setStatus] = useState<FormStatus>({ type: 'idle' });

  const validateField = (name: string, value: string): string | undefined => {
    switch (name) {
      case 'name':
        if (!value.trim()) return 'Name is required';
        if (value.trim().length < 2) return 'Name must be at least 2 characters';
        break;
      case 'email':
        if (!value.trim()) return 'Email is required';
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)) return 'Please enter a valid email';
        break;
      case 'message':
        if (!value.trim()) return 'Message is required';
        if (value.trim().length < 10) return 'Please tell me a bit more about your project';
        break;
    }
    return undefined;
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));

    if (touched[name]) {
      const error = validateField(name, value);
      setErrors((prev) => ({ ...prev, [name]: error }));
    }
  };

  const handleBlur = (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setTouched((prev) => ({ ...prev, [name]: true }));
    const error = validateField(name, value);
    setErrors((prev) => ({ ...prev, [name]: error }));
  };

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {
      name: validateField('name', formData.name),
      email: validateField('email', formData.email),
      message: validateField('message', formData.message),
    };
    setErrors(newErrors);
    setTouched({ name: true, email: true, message: true });
    return !Object.values(newErrors).some(Boolean);
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();

    if (!validateForm()) return;

    // Honeypot check - if filled, silently "succeed" without sending
    if (formData.website) {
      setStatus({
        type: 'success',
        message: 'Thanks for reaching out! I\'ll get back to you within 1 business day.',
      });
      return;
    }

    setStatus({ type: 'loading' });

    try {
      const { website, ...submitData } = formData; // exclude honeypot from submission

      // Run API call and minimum delay in parallel
      const minDelay = new Promise(resolve => setTimeout(resolve, 1500));
      const apiCall = fetch('/api/contact', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(submitData),
      });

      const [response] = await Promise.all([apiCall, minDelay]);

      if (!response.ok) {
        throw new Error('Failed to send message');
      }

      setStatus({
        type: 'success',
        message: 'Thanks for reaching out! I\'ll get back to you within 1 business day.',
      });
      setFormData({ name: '', email: '', company: '', budget: '', message: '', website: '' });
      setErrors({});
      setTouched({});
    } catch {
      setStatus({
        type: 'error',
        message: 'Something went wrong. Please try again.',
      });
    }
  };

  const handleReset = () => {
    setStatus({ type: 'idle' });
    setFormData({ name: '', email: '', company: '', budget: '', message: '', website: '' });
    setErrors({});
    setTouched({});
  };

  return (
    <>
      <Helmet>
        <title>Contact | Valendris</title>
        <meta name="description" content="Get in touch to discuss your software project. I respond within 1 business day." />
      </Helmet>
      <section className="contact">
        <div className="contact-container container">
          <div className="contact-content">
            <header className="contact-header">
              <h1 className="contact-title">Get in Touch</h1>
              <p className="contact-description">
                Have a project in mind? Tell me about it and I'll get back to you within 1 business day.
              </p>
            </header>

            {status.type === 'success' ? (
              <div className="contact-success">
                <p className="contact-success-message">{status.message}</p>
                <div className="contact-success-actions">
                  <button
                    type="button"
                    className="contact-success-button"
                    onClick={handleReset}
                  >
                    Send Another Message
                  </button>
                  <Link to="/" className="contact-success-link">
                    Back to Home
                  </Link>
                </div>
              </div>
            ) : (
              <form className="contact-form" onSubmit={handleSubmit} noValidate>
                {/* Honeypot field - hidden from users, visible to bots */}
                <input
                  type="text"
                  name="website"
                  value={formData.website}
                  onChange={handleChange}
                  className="contact-form-honeypot"
                  tabIndex={-1}
                  autoComplete="off"
                />

                <div className="contact-form-group">
                  <label htmlFor="name" className="contact-form-label">Name</label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    className={`contact-form-input ${errors.name && touched.name ? 'contact-form-input--error' : ''}`}
                    placeholder="Your name"
                  />
                  {errors.name && touched.name && (
                    <span className="contact-form-field-error">{errors.name}</span>
                  )}
                </div>

                <div className="contact-form-group">
                  <label htmlFor="email" className="contact-form-label">Email</label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    className={`contact-form-input ${errors.email && touched.email ? 'contact-form-input--error' : ''}`}
                    placeholder="you@company.com"
                  />
                  {errors.email && touched.email && (
                    <span className="contact-form-field-error">{errors.email}</span>
                  )}
                </div>

                <div className="contact-form-group">
                  <label htmlFor="company" className="contact-form-label">
                    Company <span className="contact-form-optional">(optional)</span>
                  </label>
                  <input
                    type="text"
                    id="company"
                    name="company"
                    value={formData.company}
                    onChange={handleChange}
                    className="contact-form-input"
                    placeholder="Your company"
                  />
                </div>

                <div className="contact-form-group">
                  <label htmlFor="budget" className="contact-form-label">
                    Budget Range <span className="contact-form-optional">(optional)</span>
                  </label>
                  <select
                    id="budget"
                    name="budget"
                    value={formData.budget}
                    onChange={handleChange}
                    className="contact-form-select"
                  >
                    {budgetOptions.map((option) => (
                      <option key={option.value} value={option.value}>
                        {option.label}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="contact-form-group">
                  <label htmlFor="message" className="contact-form-label">What are you trying to build?</label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    rows={5}
                    className={`contact-form-textarea ${errors.message && touched.message ? 'contact-form-input--error' : ''}`}
                    placeholder="Tell me about your project..."
                  />
                  {errors.message && touched.message && (
                    <span className="contact-form-field-error">{errors.message}</span>
                  )}
                </div>

                {status.type === 'error' && (
                  <p className="contact-form-error">{status.message}</p>
                )}

                <button
                  type="submit"
                  className="contact-form-submit"
                  disabled={status.type === 'loading'}
                >
                  {status.type === 'loading' ? 'Sending...' : 'Send Message'}
                </button>
              </form>
            )}

          </div>
        </div>
      </section>
    </>
  );
}

export default Contact;
